import { useCallback, useEffect, useState, type FormEvent } from "react";
import { Link, useParams } from "react-router-dom";
import { api, ApiError } from "../lib/api";
import { EmptyState } from "../components/EmptyState";
import { SkeletonLine } from "../components/Skeleton";
import { useToast } from "../components/Toast";
import { useConfirm } from "../components/ConfirmDialog";
import { IconArrowLeft, IconClipboard, IconPlus, IconSpinner } from "../components/Icons";
import type { ClientRecord, UserRecord, InviteRecord, UserRole } from "../types";

interface ClientDetail {
  client: ClientRecord;
  users: UserRecord[];
  pendingInvites: InviteRecord[];
}

function roleLabel(role: UserRole): string {
  if (role === "valuation_analyst") return "Analyst";
  if (role === "admin") return "Admin";
  return "Client";
}

function prettyType(t: string): string {
  return t.charAt(0).toUpperCase() + t.slice(1);
}

export default function AdminClientDetail() {
  const { id = "" } = useParams();
  const toast = useToast();
  const confirm = useConfirm();

  const [detail, setDetail] = useState<ClientDetail | null>(null);
  const [loadErr, setLoadErr] = useState<string | null>(null);

  const [showInvite, setShowInvite] = useState(false);
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteName, setInviteName] = useState("");
  const [inviteRole, setInviteRole] = useState<UserRole>("client");
  const [inviteSubmitting, setInviteSubmitting] = useState(false);
  const [inviteFormErr, setInviteFormErr] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const data = await api.get<ClientDetail>(`/api/admin/clients/${id}`);
      setDetail(data);
    } catch (e) {
      const msg = e instanceof ApiError ? e.message : "Failed to load client";
      setLoadErr(msg);
      toast.error("Client failed to load", msg);
    }
  }, [id, toast]);

  useEffect(() => { void load(); }, [load]);

  const handleInvite = async (e: FormEvent) => {
    e.preventDefault();
    setInviteFormErr(null);
    setInviteSubmitting(true);
    try {
      const resp = await api.post<{ inviteLink: string; emailDelivered: boolean }>(
        "/api/admin/invites",
        {
          email: inviteEmail.trim().toLowerCase(),
          displayName: inviteName.trim() || undefined,
          role: inviteRole,
          clientId: Number(id),
        },
      );
      if (resp.emailDelivered) {
        toast.success("Invite sent", `Emailed ${inviteEmail.trim().toLowerCase()}.`);
      } else {
        toast.warning(
          "Invite created",
          "Email couldn't be delivered (check Graph config). Copy the link from the pending invites list.",
        );
      }
      setInviteEmail("");
      setInviteName("");
      setInviteRole("client");
      setShowInvite(false);
      await load();
    } catch (e) {
      setInviteFormErr(e instanceof ApiError ? e.message : "Failed to create invite");
    } finally {
      setInviteSubmitting(false);
    }
  };

  const handleCopyLink = async (token: string) => {
    const link = `${window.location.origin}/accept/${token}`;
    try {
      await navigator.clipboard.writeText(link);
      toast.success("Invite link copied", "Paste it anywhere to share.");
    } catch {
      // Fallback: select + copy via a temp textarea
      const ta = document.createElement("textarea");
      ta.value = link;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand("copy");
      document.body.removeChild(ta);
      toast.success("Invite link copied");
    }
  };

  const handleResend = async (inviteId: number, email: string) => {
    try {
      const resp = await api.post<{ emailDelivered: boolean }>(`/api/admin/invites/${inviteId}/resend`);
      if (resp.emailDelivered) {
        toast.success("Invite re-sent", `Resent to ${email}.`);
      } else {
        toast.warning("Re-send failed", "Email couldn't be delivered. Check Graph configuration.");
      }
    } catch (e) {
      toast.error("Re-send failed", e instanceof ApiError ? e.message : "Unknown error");
    }
  };

  const handleRevoke = async (inviteId: number, email: string) => {
    const ok = await confirm({
      title: "Revoke invite",
      body: (
        <>
          The invitation link sent to <strong>{email}</strong> will stop working immediately. This cannot be undone.
        </>
      ),
      confirmLabel: "Revoke invite",
      tone: "danger",
    });
    if (!ok) return;
    try {
      await api.post(`/api/admin/invites/${inviteId}/revoke`);
      toast.success("Invite revoked");
      await load();
    } catch (e) {
      toast.error("Revoke failed", e instanceof ApiError ? e.message : "Unknown error");
    }
  };

  if (loadErr) return <div className="alert alert-error" role="alert">{loadErr}</div>;
  if (!detail) return <DetailSkeleton />;

  const { client, users, pendingInvites } = detail;

  return (
    <div>
      <div className="admin-content-header">
        <div>
          <Link to="/admin/clients" className="admin-back">
            <IconArrowLeft size={12} /> All clients
          </Link>
          <div className="admin-content-title">{client.name}</div>
          <div className="admin-content-sub">
            {prettyType(client.clientType)}
            {" · "}
            <code style={{ fontFamily: "var(--font-mono)" }}>/c/{client.slug}</code>
            {" · "}
            <span className={`badge badge-${client.active ? "active" : "inactive"}`}>
              {client.active ? "Active" : "Inactive"}
            </span>
          </div>
        </div>
        <button className="btn btn-primary" onClick={() => setShowInvite((v) => !v)}>
          {showInvite ? "Close" : <><IconPlus size={14} />Invite user</>}
        </button>
      </div>

      {showInvite && (
        <form
          onSubmit={handleInvite}
          className="card"
          style={{ marginBottom: "var(--space-5)", padding: "var(--space-4)" }}
          noValidate
        >
          <h2 style={{ fontSize: "var(--text-lg)", marginBottom: "var(--space-4)" }}>
            Invite a user to {client.name}
          </h2>
          {inviteFormErr && <div className="alert alert-error" role="alert">{inviteFormErr}</div>}

          <div className="form-group">
            <label htmlFor="invite-email">Email</label>
            <input
              id="invite-email"
              type="email"
              value={inviteEmail}
              onChange={(e) => setInviteEmail(e.target.value)}
              required
              autoFocus
              spellCheck={false}
              inputMode="email"
            />
          </div>

          <div className="form-group">
            <label htmlFor="invite-name">Display name (optional)</label>
            <input
              id="invite-name"
              type="text"
              value={inviteName}
              onChange={(e) => setInviteName(e.target.value)}
            />
          </div>

          <div className="form-group">
            <label htmlFor="invite-role">Role</label>
            <select
              id="invite-role"
              value={inviteRole}
              onChange={(e) => setInviteRole(e.target.value as UserRole)}
            >
              <option value="client">Client (owner / lender / investor)</option>
              <option value="valuation_analyst">Valuation Analyst</option>
              <option value="admin">Admin</option>
            </select>
          </div>

          <div className="form-actions">
            <button type="submit" className="btn btn-primary" disabled={inviteSubmitting}>
              {inviteSubmitting && <IconSpinner size={14} />}
              {inviteSubmitting ? "Sending…" : "Send invite"}
            </button>
            <button type="button" className="btn btn-ghost" onClick={() => setShowInvite(false)}>
              Cancel
            </button>
          </div>
        </form>
      )}

      <section className="section-block">
        <h2 className="section-heading">Users at this client</h2>
        {users.length === 0 ? (
          <EmptyState
            heading="No users yet"
            body={`Invite someone from ${client.name} to give them their own portal.`}
            action={
              <button className="btn btn-primary" onClick={() => setShowInvite(true)}>
                <IconPlus size={14} />Invite the first user
              </button>
            }
          />
        ) : (
          <table className="data-table">
            <thead>
              <tr>
                <th>Email</th>
                <th>Display name</th>
                <th>Role</th>
                <th>Last login</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {users.map((u) => (
                <tr key={u.id}>
                  <td>{u.email}</td>
                  <td>{u.displayName}</td>
                  <td>
                    <span className={`badge badge-${u.role === "valuation_analyst" ? "analyst" : u.role}`}>
                      {roleLabel(u.role)}
                    </span>
                  </td>
                  <td className="text-muted num">
                    {u.lastLogin
                      ? new Date(u.lastLogin).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" })
                      : "—"}
                  </td>
                  <td>
                    <span className={`badge badge-${u.active ? "active" : "inactive"}`}>
                      {u.active ? "Active" : "Inactive"}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>

      {pendingInvites.length > 0 && (
        <section className="section-block">
          <h2 className="section-heading">Pending invites</h2>
          <table className="data-table">
            <thead>
              <tr>
                <th>Email</th>
                <th>Role</th>
                <th>Created</th>
                <th>Expires</th>
                <th style={{ width: 320 }}></th>
              </tr>
            </thead>
            <tbody>
              {pendingInvites.map((inv) => (
                <tr key={inv.id}>
                  <td>{inv.email}</td>
                  <td>
                    <span className={`badge badge-${inv.role === "valuation_analyst" ? "analyst" : inv.role}`}>
                      {roleLabel(inv.role)}
                    </span>
                  </td>
                  <td className="text-muted num">
                    {new Date(inv.createdAt).toLocaleDateString(undefined, { month: "short", day: "numeric" })}
                  </td>
                  <td className="text-muted num">
                    {new Date(inv.expiresAt).toLocaleDateString(undefined, { month: "short", day: "numeric" })}
                  </td>
                  <td>
                    <div style={{ display: "flex", gap: "var(--space-2)", justifyContent: "flex-end" }}>
                      <button
                        className="btn btn-ghost btn-sm"
                        onClick={() => handleCopyLink(inv.token)}
                        title="Copy the invitation URL to your clipboard"
                      >
                        <IconClipboard size={12} /> Copy link
                      </button>
                      <button className="btn btn-ghost btn-sm" onClick={() => handleResend(inv.id, inv.email)}>
                        Resend
                      </button>
                      <button className="btn btn-danger btn-sm" onClick={() => handleRevoke(inv.id, inv.email)}>
                        Revoke
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      )}
    </div>
  );
}

function DetailSkeleton() {
  return (
    <div>
      <div className="admin-content-header">
        <div>
          <div style={{ marginBottom: 10 }}><SkeletonLine width={120} /></div>
          <div style={{ marginBottom: 10 }}><SkeletonLine width={280} height={28} /></div>
          <SkeletonLine width={360} />
        </div>
      </div>
      <section className="section-block">
        <h2 className="section-heading">Users at this client</h2>
        <div className="stack-2">
          <SkeletonLine width="72%" />
          <SkeletonLine width="54%" />
          <SkeletonLine width="62%" />
        </div>
      </section>
    </div>
  );
}
